import { formatQuantity } from '../../utils/inventoryLabels';

/*
 * A manual adjustment: units that enter or leave the ledger without a receipt
 * or an order behind them — a count that did not match, a broken unit, a box
 * found on the wrong shelf. It is the only write of the module that needs a
 * reason: six months later the kardex has to explain where the number went.
 */

const AVAILABLE = 'available';

export const ADJUST_DIRECTION = { ADD: 'add', REMOVE: 'remove' };

/**
 * The rows units can be taken out of: available stock, wherever it sits —
 * including what was never put away, because a loss can happen on the dock.
 *
 * @param {Array<object>} rows - The detail by location of one product.
 */
export const adjustableBuckets = (rows) => rows.filter((row) => row.state === AVAILABLE);

/** How a place reads in the "Desde" picker. */
export const adjustBucketLabel = (bucket) =>
  [
    bucket.slot_name || 'Sin ubicar',
    bucket.lot && `lote ${bucket.lot}`,
    `${formatQuantity(bucket.quantity)} ${bucket.base_uom}`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * What is still missing before the adjustment can be sent, as the sentence the
 * button shows while it is disabled — or `null` when it is ready to go.
 *
 * @param {object} form
 * @param {'add'|'remove'} form.direction
 * @param {number|string|null} form.slotId - Where the units enter (adding).
 * @param {object|null} form.from - The bucket the units leave (removing).
 * @param {string} form.quantity
 * @param {string} form.notes
 * @returns {string|null}
 */
export const adjustProblem = ({ direction, slotId, from, quantity, notes }) => {
  const removing = direction === ADJUST_DIRECTION.REMOVE;
  if (removing && !from) return 'Elige de dónde salen';
  if (!removing && !slotId) return 'Elige dónde entran';

  const amount = Number(quantity);
  if (!quantity || Number.isNaN(amount) || amount <= 0) return 'Indica cuántas unidades';
  if (removing && amount > Number(from.quantity)) {
    return `En ${from.slot_name || 'Sin ubicar'} solo hay ${formatQuantity(from.quantity)}`;
  }

  if (!notes || !notes.trim()) return 'Escribe el motivo';
  return null;
};

/**
 * The body of the adjustment. Removing names the bucket (its location and lot);
 * adding names the location and keeps the lot of the row it was opened from.
 * What was never put away has no location: the warehouse stands in for it.
 */
export const buildAdjustPayload = ({ productId, warehouseId, direction, slotId, from, lot, quantity, notes }) => {
  const removing = direction === ADJUST_DIRECTION.REMOVE;
  const slot = removing ? from.slot : slotId;
  return {
    product_id: productId,
    ...(slot ? { slot_id: slot } : { warehouse_id: warehouseId }),
    lot: (removing ? from.lot : lot) || '',
    state: AVAILABLE,
    qty_delta: removing ? `-${quantity}` : String(quantity),
    notes: notes.trim(),
  };
};

/** The sentence the page shows once the adjustment is in the kardex. */
export const adjustDoneMessage = ({ direction, sku, placeName, quantity }) =>
  direction === ADJUST_DIRECTION.REMOVE
    ? `Se quitaron ${formatQuantity(quantity)} de ${sku} en ${placeName}.`
    : `Se añadieron ${formatQuantity(quantity)} de ${sku} en ${placeName}.`;
